/**
 * SessionTimeoutModal - HIPAA idle timeout: prompt at 15 min, logout at 30 min.
 */
import { useEffect } from "react";
import { LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export { IDLE_WARN_MS, IDLE_LOGOUT_MS } from "@/lib/session-idle";
import { IDLE_LOGOUT_MS, IDLE_WARN_MS } from "@/lib/session-idle";

/** Total warning window in seconds (time between warn and logout). */
const WARN_WINDOW_S = (IDLE_LOGOUT_MS - IDLE_WARN_MS) / 1000;

const RING_SIZE = 148;
const RING_STROKE = 8;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

interface SessionTimeoutModalProps {
  open: boolean;
  onStaySignedIn: () => void;
  onLogout: () => void;
  secondsUntilLogout: number;
}

export function SessionTimeoutModal({
  open,
  onStaySignedIn,
  onLogout,
  secondsUntilLogout,
}: SessionTimeoutModalProps) {
  const remaining = Math.max(0, secondsUntilLogout);
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  // Fraction of the warning window left (1 → full, 0 → signed out).
  const fraction = Math.max(0, Math.min(1, remaining / WARN_WINDOW_S));
  const urgent = remaining <= 60;

  useEffect(() => {
    if (!open || secondsUntilLogout > 0) return;
    onLogout();
  }, [open, secondsUntilLogout, onLogout]);

  return (
    // Dismissing (Escape / backdrop) counts as activity: keep the session.
    <Dialog open={open} onOpenChange={(next) => !next && onStaySignedIn()}>
      <DialogContent hideCloseButton className="overflow-hidden rounded-3xl border border-border p-0 shadow-2xl sm:max-w-[380px]">
        <div
          className={cn(
            "relative flex flex-col items-center px-8 pb-8 pt-9 text-center",
            "bg-gradient-to-b to-transparent to-60%",
            urgent ? "from-rose-500/10" : "from-amber-500/10",
          )}
        >
          {/* Countdown ring */}
          <div className="relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
            <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90" aria-hidden="true">
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                className="stroke-muted"
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={`${fraction * RING_CIRC} ${RING_CIRC}`}
                className={cn(
                  "transition-[stroke-dasharray,stroke] duration-1000 ease-linear",
                  urgent ? "stroke-rose-500" : "stroke-amber-500",
                )}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className={cn(
                  "text-4xl font-bold tabular-nums tracking-tight",
                  urgent ? "text-rose-600 dark:text-rose-400" : "text-foreground",
                )}
              >
                {minutes}:{seconds.toString().padStart(2, "0")}
              </span>
              <span className="mt-0.5 text-[11px] font-medium text-muted-foreground">
                {urgent ? "seconds left" : "until sign-out"}
              </span>
            </div>
          </div>

          <DialogTitle className="mt-6 text-xl font-bold tracking-tight text-foreground">
            Are you still there?
          </DialogTitle>
          <DialogDescription className="mt-2 max-w-[17rem] text-sm leading-relaxed text-muted-foreground">
            You&apos;ve been inactive for a while. To protect patient information, we&apos;ll sign you out
            automatically.
          </DialogDescription>

          <div className="mt-7 grid w-full gap-2.5">
            <Button autoFocus className="h-11 w-full rounded-xl text-sm font-semibold" onClick={onStaySignedIn}>
              Stay signed in
            </Button>
            <Button
              variant="ghost"
              className="h-10 w-full gap-1.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={onLogout}
            >
              <LogOut className="h-4 w-4" />
              Sign out now
            </Button>
          </div>

          <p className="mt-5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Unsaved changes on this page are kept if you stay.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
